import * as React from 'react';

import {
  AssetTypeSortOption,
  enumAssetTypeSortOption,
} from '@rematter/frontend-lib-api-client';
import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { Button, IconButton, NumberField, Select } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { Plus, Trash2 } from 'lucide-react';

import { useAssetTypesQuery } from '../../hooks/queries/useAssetTypesQuery';
import { useServiceTypeTaskDefinitionsQuery } from '../../hooks/queries/useServiceTypeTaskDefinitionsQuery';
import {
  $serviceForm,
  $updateServiceForm,
} from '../../state/serviceForm.localState';

import { ServiceAssetQuantity } from '~/modules/transportation/types';

type TaskStep = {
  sequence: number;
  taskType: string;
  locationRef: string;
};

type TaskStepsListProps = {
  taskSteps: TaskStep[];
  taskTypeLabels: Record<string, string>;
  locationRefLabels: Record<string, string>;
};

const TaskStepsList = ({
  taskSteps,
  taskTypeLabels,
  locationRefLabels,
}: TaskStepsListProps) => {
  if (taskSteps.length === 0) return null;
  return (
    <ol className="@TaskStepsList flex flex-col gap-0.5 text-xs text-neutral/50">
      {taskSteps.map((step) => {
        const typeLabel = taskTypeLabels[step.taskType] ?? step.taskType;
        const locLabel = step.locationRef
          ? locationRefLabels[step.locationRef]
          : undefined;
        return (
          <li key={step.sequence}>
            {step.sequence}. {typeLabel}
            {locLabel ? ` at ${locLabel}` : ''}
          </li>
        );
      })}
    </ol>
  );
};

type AssetTypeOption = {
  key: string;
  label: string;
};

type AssetTypeEntry = {
  id: string;
  name: string;
};

type AssetQuantityRowsProps = {
  rows: { row: ServiceAssetQuantity; originalIndex: number }[];
  group: number;
  assetTypes: AssetTypeEntry[];
  isReadOnly?: boolean;
  getAssetTypeOptions: (group: number) => AssetTypeOption[];
  onUpdate: (index: number, update: Partial<ServiceAssetQuantity>) => void;
  onRemove: (index: number) => void;
  onAdd: (group: number) => void;
  labels: {
    none: string;
    assetType: string;
    quantity: string;
    add: string;
  };
};

const AssetQuantityRows = ({
  rows,
  group,
  assetTypes,
  isReadOnly,
  getAssetTypeOptions,
  onUpdate,
  onRemove,
  onAdd,
  labels,
}: AssetQuantityRowsProps) => (
  <>
    {rows.length === 0 && (
      <div className="text-sm text-neutral/50">{labels.none}</div>
    )}

    {rows.map(({ row, originalIndex }) => (
      <div key={originalIndex} className="flex items-end gap-2">
        <div className="flex-1">
          <Select
            label={labels.assetType}
            value={row.assetType?.id ?? ''}
            onChange={(value) => {
              const assetType = assetTypes.find((at) => at.id === value);
              onUpdate(originalIndex, {
                assetType: assetType
                  ? { id: assetType.id, name: assetType.name }
                  : undefined,
              });
            }}
            options={[
              ...(row.assetType?.id
                ? [{ key: row.assetType.id, label: row.assetType.name ?? '' }]
                : []),
              ...getAssetTypeOptions(group),
            ]}
            isDisabled={isReadOnly}
          />
        </div>
        <div className="w-24">
          <NumberField
            label={labels.quantity}
            value={row.quantity}
            onChange={(value) =>
              onUpdate(originalIndex, { quantity: value ?? 1 })
            }
            isDisabled={isReadOnly}
            minValue={1}
          />
        </div>
        {!isReadOnly && (
          <IconButton
            icon={<Trash2 size={18} />}
            variant="outline"
            color="failure"
            className="mb-0.5 size-[36px]"
            onPress={() => onRemove(originalIndex)}
          />
        )}
      </div>
    ))}

    {!isReadOnly && (
      <Button
        variant="outline"
        color="primary"
        onPress={() => onAdd(group)}
        className="w-fit"
      >
        <Plus size={16} />
        {labels.add}
      </Button>
    )}
  </>
);

type ServiceAssetSectionProps = {
  isReadOnly?: boolean;
  serviceTypeId?: string;
};

export const ServiceAssetSection = ({
  isReadOnly,
  serviceTypeId,
}: ServiceAssetSectionProps) => {
  const t = useTranslations();
  const form = useAtomValue($serviceForm);
  const updateForm = useSetAtom($updateServiceForm);

  const { data } = useAssetTypesQuery({
    sorting: [enumAssetTypeSortOption.NameAsc as AssetTypeSortOption],
    limit: 100,
  });

  const { data: taskDefData } = useServiceTypeTaskDefinitionsQuery({
    filter: serviceTypeId ? { serviceTypeId } : undefined,
    pagination: { offset: 0, limit: 100 },
    queryOptions: { enabled: !!serviceTypeId },
  });

  type GroupInfo = {
    group: number;
    header: string;
    taskSteps: { sequence: number; taskType: string; locationRef: string }[];
  };

  const taskTypeLabels = t.serviceTypes.dialog.taskDefinitions
    .taskTypes as Record<string, string>;
  const locationRefLabels = t.servicesView.form.fields.assets.locationRefs;

  const groupInfos = React.useMemo((): GroupInfo[] => {
    const defs = taskDefData?.taskDefinitions ?? [];
    const groups = [
      ...new Set(
        defs.map((d) => d.assetGroup).filter((g): g is number => g != null),
      ),
    ];
    groups.sort((a, b) => a - b);
    const effectiveGroups = groups.length > 0 ? groups : [1];
    const isMulti = effectiveGroups.length > 1;

    return effectiveGroups.map((group) => {
      const groupDefs = defs
        .filter((d) => d.assetGroup === group)
        .sort((a, b) => a.sequence - b.sequence);

      const serviceLocationDef = groupDefs.find(
        (d) => d.locationRef === 'service_location',
      );

      const header =
        isMulti && serviceLocationDef
          ? (taskTypeLabels[serviceLocationDef.taskType] ??
            serviceLocationDef.taskType)
          : t.servicesView.form.fields.assets.assets;

      const taskSteps = groupDefs.map((d) => ({
        sequence: d.sequence,
        taskType: d.taskType,
        locationRef: d.locationRef ?? '',
      }));

      return { group, header, taskSteps };
    });
  }, [taskDefData?.taskDefinitions, taskTypeLabels, locationRefLabels, t]);

  const isMultiGroup = groupInfos.length > 1;

  const getAssetTypeOptions = (group: number) => {
    const selectedIds = new Set(
      form.assetQuantities
        ?.filter((aq) => aq.assetGroup === group)
        .map((aq) => aq.assetType?.id)
        .filter(Boolean),
    );
    return (data?.assetTypes ?? [])
      .filter((at) => !selectedIds.has(at.id))
      .map((at) => ({
        key: at.id,
        label: at.name,
      }));
  };

  const handleAdd = (group: number) => {
    const current = form.assetQuantities ?? [];
    updateForm({
      assetQuantities: [...current, { quantity: 1, assetGroup: group }],
    });
  };

  const handleRemove = (index: number) => {
    const current = form.assetQuantities ?? [];
    updateForm({
      assetQuantities: current.filter((_, i) => i !== index),
    });
  };

  const handleUpdate = (
    index: number,
    update: Partial<ServiceAssetQuantity>,
  ) => {
    const current = form.assetQuantities ?? [];
    updateForm({
      assetQuantities: current.map((item, i) =>
        i === index ? { ...item, ...update } : item,
      ),
    });
  };

  const allRows = form.assetQuantities ?? [];

  const assetTypes: AssetTypeEntry[] = React.useMemo(
    () => (data?.assetTypes ?? []).map((at) => ({ id: at.id, name: at.name })),
    [data?.assetTypes],
  );

  const assetLabels = React.useMemo(
    () => ({
      none: t.servicesView.form.fields.assets.none,
      assetType: t.servicesView.form.fields.assets.assetType,
      quantity: t.servicesView.form.fields.assets.quantity,
      add: t.servicesView.form.fields.assets.add,
    }),
    [t],
  );

  if (!isMultiGroup) {
    const info = groupInfos[0];
    const rows = allRows.map((row, i) => ({ row, originalIndex: i }));

    return (
      <div className="@ServiceAssetSection flex flex-col gap-2">
        <div className="text-sm font-medium text-neutral/70">
          {t.servicesView.form.fields.assets.assets}
        </div>
        <TaskStepsList
          taskSteps={info.taskSteps}
          taskTypeLabels={taskTypeLabels}
          locationRefLabels={locationRefLabels}
        />
        <AssetQuantityRows
          rows={rows}
          group={info.group}
          assetTypes={assetTypes}
          isReadOnly={isReadOnly}
          getAssetTypeOptions={getAssetTypeOptions}
          onUpdate={handleUpdate}
          onRemove={handleRemove}
          onAdd={handleAdd}
          labels={assetLabels}
        />
      </div>
    );
  }

  return (
    <div className="@ServiceAssetSection flex flex-col gap-4">
      <div className="text-sm font-medium text-neutral/70">
        {t.servicesView.form.fields.assets.assets}
      </div>
      {groupInfos.map((info) => {
        const groupRows = allRows
          .map((row, i) => ({ row, originalIndex: i }))
          .filter(({ row }) => row.assetGroup === info.group);

        return (
          <div
            key={info.group}
            className="flex flex-col gap-2 rounded-md border border-neutral/20 p-3"
          >
            <div className="text-sm font-medium text-neutral/60">
              {info.header}
            </div>
            <TaskStepsList
              taskSteps={info.taskSteps}
              taskTypeLabels={taskTypeLabels}
              locationRefLabels={locationRefLabels}
            />
            <AssetQuantityRows
              rows={groupRows}
              group={info.group}
              assetTypes={assetTypes}
              isReadOnly={isReadOnly}
              getAssetTypeOptions={getAssetTypeOptions}
              onUpdate={handleUpdate}
              onRemove={handleRemove}
              onAdd={handleAdd}
              labels={assetLabels}
            />
          </div>
        );
      })}
    </div>
  );
};
